/*
 * nco-collection-filters.js
 *
 * Client-side filtering engine for collection pages — variant-aware,
 * with live counts. Reads the JSON product index rendered by
 * snippets/nco-collection-filters.liquid and performs all filtering
 * in the browser. Emits nco:filter:change custom events for the
 * collection grid to consume.
 *
 * Architecture:
 *   - AND logic between filter groups
 *   - OR logic within a filter group
 *   - Matrix-based matching: products are visible only if a real
 *     variant combo satisfies all active option filters simultaneously
 *   - Cross-filter live counts
 *   - Zero-count options disabled to prevent dead ends
 *   - URL state sync via history.replaceState
 */

const SELECTORS = {
  container: '[data-nco-filters]',
  index: '#nco-product-index',
  group: '[data-nco-filter-group]',
  groupHeader: '.nco-filters__group-header',
  options: '[data-nco-filter-options]',
  option: '[data-nco-filter-value]',
  chips: '[data-nco-filter-chips]',
  clearAll: '[data-nco-filter-clear-all]',
  chip: '[data-nco-chip]',
  chipRemove: '[data-nco-chip-remove]',
};

const STATE = {
  active: 'nco-filters__option--active',
  disabled: 'nco-filters__option--disabled',
  collapsed: 'nco-filters__group--collapsed',
};

/* ------------------------------------------------------------------ */
/* Band definitions for range filters                                  */
/* ------------------------------------------------------------------ */

const WIDTH_BANDS = [
  { label: 'Under 400mm', min: 0, max: 399, key: 'under-400' },
  { label: '400–600mm', min: 400, max: 600, key: '400-600' },
  { label: '600–800mm', min: 601, max: 800, key: '600-800' },
  { label: '800mm+', min: 801, max: Infinity, key: '800-plus' },
];

const DEPTH_BANDS = [
  { label: 'Under 300mm', min: 0, max: 299, key: 'under-300' },
  { label: '300–400mm', min: 300, max: 400, key: '300-400' },
  { label: '400–500mm', min: 401, max: 500, key: '400-500' },
  { label: '500mm+', min: 501, max: Infinity, key: '500-plus' },
];

const PRICE_BANDS = [
  { label: 'Under $1,000', min: 0, max: 999, key: 'under-1000' },
  { label: '$1,000–$1,500', min: 1000, max: 1500, key: '1000-1500' },
  { label: '$1,500–$3,000', min: 1501, max: 3000, key: '1500-3000' },
  { label: '$3,000+', min: 3001, max: Infinity, key: '3000-plus' },
];

const URL_PARAM_ORDER = [
  'colour', 'mount', 'size', 'finish', 'tray',
  'overflow', 'tapHole', 'overflowFinish',
  'widthBand', 'depthBand', 'priceBand', 'shapeStyle', 'roomType'
];

const FILTER_TO_URL_KEY = {
  colour: 'colour',
  mountType: 'mount',
  overflow: 'overflow',
  tapHole: 'tapHole',
  tray: 'tray',
  finish: 'finish',
  shapeStyle: 'shapeStyle',
  widthBand: 'widthBand',
  depthBand: 'depthBand',
  priceBand: 'priceBand',
  roomType: 'roomType',
};

const URL_KEY_TO_FILTER = {};
Object.keys(FILTER_TO_URL_KEY).forEach(function (k) {
  URL_KEY_TO_FILTER[FILTER_TO_URL_KEY[k]] = k;
});

/* ------------------------------------------------------------------ */
/* Product index & filter state                                        */
/* ------------------------------------------------------------------ */

let products = [];
const activeFilters = {};
let allOptions = {};

/* ------------------------------------------------------------------ */
/* Utility                                                             */
/* ------------------------------------------------------------------ */

function handleize(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function escapeHTML(str) {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(str));
  return div.innerHTML;
}

function escapeAttr(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatPrice(cents) {
  return '$' + (cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/* The collection filter is rendered as a dependency of nco-collection.js. Keep the
   predicate here as a defensive compatibility guard for older templates that may still
   include the module directly as well: two module URLs can create two independent module
   instances and duplicate every delegated click/change handler. */
var NCO_FILTER_INIT_KEY = '__ncoCollectionFiltersInitialised';
function isBasinProduct(product) {
  var type = String(product && product.productType || '').trim().toLowerCase();
  if (type) return type === 'basin';
  /* Backward-compatible fallback for cached/older indexes that predate productType. */
  var identity = String(product && (product.handle || product.name) || '').toLowerCase();
  return /(^|[-\s])basins?($|[-\s])/.test(identity) || /-basin$/.test(identity);
}

/* ------------------------------------------------------------------ */
/* Matrix-based combo matching                                         */
/* ------------------------------------------------------------------ */

/* C2 finish facet: the PDP alias map is permanent (Raw <-> Raw Textured, Signature <->
   Signature Smooth; 12 live homepage links use ?finish=Raw). Every comparison in this
   engine runs through this normaliser so both spellings select the same facet value. */
var FINISH_CANONICAL = {
  'raw': 'Raw Textured',
  'raw textured': 'Raw Textured',
  'signature': 'Signature Smooth',
  'signature smooth': 'Signature Smooth',
};
function normaliseFinish(v) {
  var key = String(v || '').trim().toLowerCase();
  return FINISH_CANONICAL[key] || String(v || '').trim();
}
function finishValuesMatch(selected, candidates) {
  var wanted = selected.map(normaliseFinish);
  return (candidates || []).some(function (c) { return wanted.indexOf(normaliseFinish(c)) !== -1; });
}

function comboMatchesFilters(combo, product, filters) {
  var fm = product.filterMatrix;

  var colourValues = filters.colour;
  if (colourValues && colourValues.length > 0) {
    var comboColours = combo.availableColours || fm.colours || [];
    var hasColourMatch = colourValues.some(function (v) {
      return comboColours.indexOf(handleize(v)) !== -1;
    });
    if (!hasColourMatch) return false;
  }

  var mountValues = filters.mountType;
  if (mountValues && mountValues.length > 0) {
    var comboMount = combo.optionParams.mount;
    if (!comboMount) return false;
    if (mountValues.indexOf(comboMount) === -1) return false;
  }

  var overflowValues = filters.overflow;
  if (overflowValues && overflowValues.length > 0) {
    var comboOverflow = combo.optionParams.overflow;
    if (!comboOverflow) {
      /* A matrix can describe tray/size combinations without having an overflow
         axis at all. For a basin family with no overflow values anywhere, that
         combination is a fixed no-overflow product rather than an unknown row. */
      if (!(overflowValues.indexOf('No Overflow') !== -1 &&
            !(product.overflow || []).length && isBasinProduct(product))) return false;
      comboOverflow = 'No Overflow';
    }
    if (overflowValues.indexOf(comboOverflow) === -1) return false;
  }

  var tapHoleValues = filters.tapHole;
  if (tapHoleValues && tapHoleValues.length > 0) {
    var comboTapHole = combo.optionParams.tapHole;
    if (!comboTapHole) return false;
    var mapped = tapHoleValues.map(function (v) {
      return v === 'Single Tap Hole' ? '1 Tap Hole' : v;
    });
    if (mapped.indexOf(comboTapHole) === -1) return false;
  }

  var trayValues = filters.tray;
  if (trayValues && trayValues.length > 0) {
    var comboTray = combo.optionParams.tray;
    if (!comboTray) return false;
    if (trayValues.indexOf(comboTray) === -1) return false;
  }

  var finishValues = filters.finish;
  if (finishValues && finishValues.length > 0) {
    // Combo-level finish wins; a matrix without a finish axis falls back to the family's
    // declared finish list; a family declaring no finish never matches a finish filter.
    var comboFinish = combo.optionParams.finish;
    if (comboFinish) {
      if (!finishValuesMatch(finishValues, [comboFinish])) return false;
    } else if (!finishValuesMatch(finishValues, product.finish)) {
      return false;
    }
  }

  var priceValues = filters.priceBand;
  if (priceValues && priceValues.length > 0) {
    var comboPrice = combo.priceMin / 100;
    var priceMatch = priceValues.some(function (bandKey) {
      var band = PRICE_BANDS.find(function (b) { return b.key === bandKey; });
      return band && comboPrice >= band.min && comboPrice <= band.max;
    });
    if (!priceMatch) return false;
  }

  return true;
}

function getMatchingCombos(product, filters) {
  var fm = product.filterMatrix;
  if (!fm || !fm.combos) return null;
  var combos = Object.values(fm.combos);
  return combos.filter(function (c) {
    return comboMatchesFilters(c, product, filters);
  });
}

/* ------------------------------------------------------------------ */
/* Matching logic                                                      */
/* ------------------------------------------------------------------ */

var MATRIX_GROUPS = ['colour', 'mountType', 'overflow', 'tapHole', 'tray', 'finish', 'priceBand'];
var BOOLEAN_GROUPS = ['shapeStyle', 'widthBand', 'depthBand', 'roomType'];
/* F5 (staff feedback 2026-09-01): finish is single-select — picking one finish replaces the
   other; clicking the active finish clears it (handled by toggleFilter's existing paths). */
var SINGLE_SELECT_GROUPS = { colour: true, tapHole: true, overflow: true, tray: true, finish: true };

function productMatchesGroup(product, groupKey, values) {
  if (!values || values.length === 0) return true;

  switch (groupKey) {
    case 'colour':
      return values.some(function (v) {
        return (product.colours || []).some(function (c) {
          return (typeof c === 'string' ? c : c.name) === v;
        });
      });

    case 'mountType':
      return values.some(function (v) {
        return (product.mountTypes || []).indexOf(v) !== -1;
      });

    case 'shapeStyle':
      return values.some(function (v) {
        return (product.shapes || []).indexOf(v) !== -1 || (product.styles || []).indexOf(v) !== -1;
      });

    case 'widthBand':
      return values.some(function (bandKey) {
        var band = WIDTH_BANDS.find(function (b) { return b.key === bandKey; });
        if (!band) return false;
        return (product.widths || []).some(function (w) { return w >= band.min && w <= band.max; });
      });

    case 'depthBand':
      return values.some(function (bandKey) {
        var band = DEPTH_BANDS.find(function (b) { return b.key === bandKey; });
        if (!band) return false;
        return (product.depths || []).some(function (d) { return d >= band.min && d <= band.max; });
      });

    case 'priceBand':
      return values.some(function (bandKey) {
        var band = PRICE_BANDS.find(function (b) { return b.key === bandKey; });
        if (!band) return false;
        return (product.prices || []).some(function (p) { return p >= band.min && p <= band.max; });
      });

    case 'overflow':
      return values.some(function (v) {
        if (v === 'No Overflow') {
          /* Kelwyn's filter is customer-facing: a basin with no overflow choice is
             itself a no-overflow product. Keep the explicit Shelf/Loop configuration
             rows, and include basin families whose catalogue has no overflow axis at
             all. Products such as mixers and samples have no mountTypes and remain
             outside this basin-oriented facet. */
          return (product.overflow || []).indexOf(v) !== -1 ||
            (!(product.overflow || []).length && isBasinProduct(product));
        }
        return (product.overflow || []).indexOf(v) !== -1;
      });

    case 'tapHole':
      return values.some(function (v) {
        return (product.tapHole || []).indexOf(v) !== -1;
      });

    case 'tray':
      return values.some(function (v) {
        return (product.tray || []).indexOf(v) !== -1;
      });

    case 'finish':
      return finishValuesMatch(values, product.finish);

    case 'roomType':
      return values.some(function (v) {
        return (product.roomTypes || []).indexOf(v) !== -1;
      });

    default:
      return true;
  }
}

function hasActiveMatrixFilters(filters) {
  for (var i = 0; i < MATRIX_GROUPS.length; i++) {
    var key = MATRIX_GROUPS[i];
    if (filters[key] && filters[key].length > 0) return true;
  }
  return false;
}

function productMatchesAllFilters(product, filters) {
  // Boolean-only groups: always use simple matching
  for (var i = 0; i < BOOLEAN_GROUPS.length; i++) {
    var groupKey = BOOLEAN_GROUPS[i];
    var values = filters[groupKey];
    if (values && values.length > 0 && !productMatchesGroup(product, groupKey, values)) {
      return false;
    }
  }

  // Matrix-aware groups
  if (!hasActiveMatrixFilters(filters)) return true;

  var fm = product.filterMatrix;
  if (!fm) {
    // No matrix: fall back to boolean matching for matrix groups
    for (var j = 0; j < MATRIX_GROUPS.length; j++) {
      var gk = MATRIX_GROUPS[j];
      var vals = filters[gk];
      if (vals && vals.length > 0 && !productMatchesGroup(product, gk, vals)) {
        return false;
      }
    }
    return true;
  }

  // Has matrix: at least one combo must satisfy all active matrix-group constraints
  var matching = getMatchingCombos(product, filters);
  return matching && matching.length > 0;
}

function getMatchingProducts(filters) {
  return products.filter(function (p) {
    return productMatchesAllFilters(p, filters);
  });
}

function getVisibleIds() {
  return getMatchingProducts(activeFilters).map(function (p) { return p.id; });
}

/* ------------------------------------------------------------------ */
/* Variant-aware price/URL resolution                                  */
/* ------------------------------------------------------------------ */

/* Convert a filter_matrix combo's optionParams to a configuration_image_map key
   ("<mount>-<NO|WO>-<0|1>", e.g. "1-NO-0"). Returns null unless all three segments are
   the recognised Shelf/Loop shape — so cylinders/tray combos yield no key and the card keeps
   its colour-map/default behaviour. overflowFinish is intentionally ignored (the config map is
   finish-agnostic; the collection has no overflow-finish facet). */
function ncoComboConfigKey(op) {
  if (!op) return null;
  var m = op.mount === 'Surface' ? '1' : (op.mount === 'Wall' ? '2' : null);
  var o = op.overflow === 'No Overflow' ? 'NO' : (op.overflow === 'Overflow' ? 'WO' : null);
  var t = op.tapHole === 'No Tap Hole' ? '0' : (op.tapHole === '1 Tap Hole' ? '1' : null);
  return (m && o && t) ? (m + '-' + o + '-' + t) : null;
}

/* Canonical option signature for a filter_matrix combo, e.g. "mount=Wall|size=Standard".
   Axis order is FIXED so the Liquid emitter and this reader always agree; keys are the lowercase
   option names Shopify already uses in optionParams. Returns null when no axis is present, so
   colour-only products (no Mount/Size axes) never enter the native mount-aware path. */
var NCO_COMBO_AXES = ['mount', 'size', 'finish', 'tone', 'tray'];
function ncoComboSignature(op) {
  if (!op) return null;
  var parts = [];
  for (var i = 0; i < NCO_COMBO_AXES.length; i++) {
    var a = NCO_COMBO_AXES[i];
    if (op[a]) parts.push(a + '=' + op[a]);
  }
  return parts.length ? parts.join('|') : null;
}

/* Exact variant join. filter_matrix is intentionally aggregate data (one configuration can
   apply to many colours), so a price-matched combo is not itself a variant. Native products
   carry a compact signature+colour map; high-variant products carry the uncapped variant_index
   and option names. Every caller must treat a miss as a data-quality signal, never invent an id. */
function ncoNormaliseVariantValue(axis, value) {
  var v = String(value || '').trim();
  if (axis === 'finish') return normaliseFinish(v).toLowerCase();
  return v.toLowerCase();
}

function ncoVariantIndexParams(optionNames, parts) {
  var out = {};
  for (var i = 0; i < optionNames.length; i++) {
    var name = String(optionNames[i] || '').toLowerCase();
    var value = parts[i] || '';
    if (!value) continue;
    if (name === 'colour' || name === 'color') out.colour = value;
    else if (name === 'mount' || name === 'mount type') out.mount = value;
    else if (name === 'size' || name === 'width' || name === 'depth') out.size = value;
    else if (name === 'finish') out.finish = value;
    else if (name === 'tone' || name === 'tone mode') out.tone = value;
    else if (name === 'tray' || name === 'tray colour') out.tray = value;
    else if (name === 'configuration') {
      /* Configuration labels include the tap-hole token before the overflow token
         (for example "No Tap Hole - No Overflow"), so detect the overflow state
         anywhere in the label rather than only at the start. */
      out.overflow = value.toLowerCase().indexOf('no overflow') !== -1 ? 'No Overflow' : 'Overflow';
      out.tapHole = value.indexOf('1 Tap Hole') !== -1 ? '1 Tap Hole' : 'No Tap Hole';
      var of = value.split('Overflow - ')[1] || '';
      if (of) out.overflowFinish = of;
    }
  }
  return out;
}

function ncoVariantParamsMatch(candidate, wanted, colourName) {
  var axes = ['colour', 'mount', 'size', 'finish', 'tone', 'tray', 'overflow', 'tapHole', 'overflowFinish'];
  var expected = Object.assign({}, wanted || {});
  if (colourName) expected.colour = colourName;
  for (var i = 0; i < axes.length; i++) {
    var axis = axes[i];
    if (!expected[axis]) continue;
    var left = ncoNormaliseVariantValue(axis, candidate[axis]);
    var right = ncoNormaliseVariantValue(axis, expected[axis]);
    if (axis === 'colour' || axis === 'tray') {
      left = handleize(candidate[axis]);
      right = handleize(expected[axis]);
    }
    if (!left || left !== right) return false;
  }
  return true;
}

function ncoVariantIdFor(product, optionParams, colourName) {
  var sig = ncoComboSignature(optionParams);
  var colourHandle = handleize(colourName || (optionParams && optionParams.colour));
  if (product.variantIdMap && sig && colourHandle) {
    var exact = product.variantIdMap[sig + '~' + colourHandle];
    if (exact) return Number(exact);
  }
  if (product.variantColourIdMap && colourHandle && (!sig || Object.keys(optionParams || {}).length === 0)) {
    var byColour = product.variantColourIdMap[colourHandle];
    if (byColour) return Number(byColour);
  }
  if (product.variantIndex && product.variantOptionNames) {
    var keys = Object.keys(product.variantIndex).sort();
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var entry = product.variantIndex[key];
      if (!entry || !entry.id) continue;
      var candidate = ncoVariantIndexParams(product.variantOptionNames, key.split('|'));
      if (ncoVariantParamsMatch(candidate, optionParams, colourName)) return Number(entry.id);
    }
  }
  return null;
}

/* Return the colour names that are actually available for one representative
   configuration. High-variant products cannot rely on the first product colour:
   their uncapped variant index is the source of truth. */
function ncoVariantColoursFor(product, optionParams) {
  if (!product || !product.variantIndex || !product.variantOptionNames) return null;

  var found = {};
  var keys = Object.keys(product.variantIndex);
  for (var i = 0; i < keys.length; i++) {
    var entry = product.variantIndex[keys[i]];
    if (!entry || !entry.id) continue;
    var candidate = ncoVariantIndexParams(product.variantOptionNames, keys[i].split('|'));
    if (!candidate.colour || !ncoVariantParamsMatch(candidate, optionParams, null)) continue;
    var handle = handleize(candidate.colour);
    if (!found[handle]) found[handle] = candidate.colour;
  }
  return found;
}

function resolveFilteredVariant(product, filters) {
  var result = {
    price: product.priceMin || 0,
    url: '/products/' + product.handle,
    isRange: true,
    variantId: null,
    exactVariant: false,
    matched: false,
    selectedOptions: null,
  };

  var fm = product.filterMatrix;
  if (!fm || !hasActiveMatrixFilters(filters)) {
    result.matched = productMatchesAllFilters(product, filters);
    return result;
  }

  var matching = getMatchingCombos(product, filters);
  if (!matching || matching.length === 0) return result;
  result.matched = true;

  var prices = [];
  for (var i = 0; i < matching.length; i++) {
    prices.push(matching[i].priceMin);
    if (matching[i].priceMax !== matching[i].priceMin) {
      prices.push(matching[i].priceMax);
    }
  }

  var minPrice = Math.min.apply(null, prices);
  var maxPrice = Math.max.apply(null, prices);
  result.price = minPrice;
  result.isRange = minPrice !== maxPrice;

  // Representative config for the card image: the lowest-priced matching combo (insertion-order
  // tie-break), converted to a configuration_image_map key. Keeps the card image aligned with the
  // displayed (minimum) price + link. null for non-config (cylinder/tray) products.
  var rep = null;
  for (var ri = 0; ri < matching.length; ri++) {
    if (matching[ri].priceMin === minPrice) { rep = matching[ri]; break; }
  }
  if (!rep) {
    rep = matching[0];
    for (var rj = 1; rj < matching.length; rj++) {
      if (matching[rj].priceMin < rep.priceMin) rep = matching[rj];
    }
  }
  result.cardConfigKey = ncoComboConfigKey(rep && rep.optionParams);
  /* Canonical option signature for the SAME representative combo, used by native (<=250 variant)
     cards to pick a mount/size-accurate image. Mirrors the Liquid builder in
     snippets/nco-collection-filters.liquid: fixed axis order, "axis=Value" joined by "|". */
  result.cardComboSig = ncoComboSignature(rep && rep.optionParams);

  var validVariantColours = ncoVariantColoursFor(product, rep && rep.optionParams);
  var repColour = null;
  if (filters.colour && filters.colour.length === 1 &&
      (!validVariantColours || validVariantColours[handleize(filters.colour[0])])) {
    repColour = filters.colour[0];
  } else if (result.cardComboSig && product.variantComboRep) {
    var mappedRep = product.variantComboRep[result.cardComboSig] || null;
    if (!validVariantColours || validVariantColours[handleize(mappedRep)]) repColour = mappedRep;
  }
  if (!repColour && product.colours && product.colours.length) {
    for (var ci = 0; ci < product.colours.length; ci++) {
      var candidateColour = typeof product.colours[ci] === 'string' ? product.colours[ci] : product.colours[ci].name;
      if (!validVariantColours || validVariantColours[handleize(candidateColour)]) {
        repColour = candidateColour;
        break;
      }
    }
  }
  if (!repColour && validVariantColours) {
    var validHandles = Object.keys(validVariantColours);
    if (validHandles.length) repColour = validVariantColours[validHandles[0]];
  }
  result.selectedOptions = Object.assign({}, (rep && rep.optionParams) || {});
  if (repColour) result.selectedOptions.colour = repColour;
  result.variantId = ncoVariantIdFor(product, result.selectedOptions, repColour);
  result.exactVariant = !!result.variantId;

  // Build URL with option params (single-value filters only)
  var params = [];
  var colourVals = filters.colour;
  if (colourVals && colourVals.length === 1) {
    params.push('colour=' + encodeURIComponent(colourVals[0]));
  }
  var mountVals = filters.mountType;
  if (mountVals && mountVals.length === 1 && mountVals[0] !== 'Freestanding') {
    params.push('mount=' + encodeURIComponent(mountVals[0]));
  }
  var overflowVals = filters.overflow;
  if (overflowVals && overflowVals.length === 1) {
    params.push('overflow=' + encodeURIComponent(overflowVals[0]));
  }
  var tapHoleVals = filters.tapHole;
  if (tapHoleVals && tapHoleVals.length === 1) {
    var thVal = tapHoleVals[0];
    if (thVal === 'Single Tap Hole' || thVal === '1 Tap Hole') thVal = 'Yes';
    else if (thVal === 'No Tap Hole') thVal = 'No';
    params.push('tapHole=' + encodeURIComponent(thVal));
  }
  var trayVals = filters.tray;
  if (trayVals && trayVals.length === 1) {
    params.push('tray=' + encodeURIComponent(trayVals[0]));
  }
  // C2: carry the chosen finish through to the PDP (the ?finish alias is permanent) so a card
  // matched on Raw Textured lands on a PDP with that finish selected, not the default.
  var finishVals = filters.finish;
  if (finishVals && finishVals.length === 1) {
    params.push('finish=' + encodeURIComponent(normaliseFinish(finishVals[0]) || finishVals[0]));
  }

  if (result.variantId) {
    result.url = '/products/' + product.handle + '?variant=' + encodeURIComponent(result.variantId);
  } else if (params.length > 0) {
    result.url = '/products/' + product.handle + '?' + params.join('&');
  }

  if (!result.variantId && hasActiveMatrixFilters(filters) && typeof console !== 'undefined' && console.warn) {
    console.warn('[nco] exact variant join miss', product.handle, result.selectedOptions);
  }

  return result;
}

/* ------------------------------------------------------------------ */
/* Cross-filter count calculation                                      */
/* ------------------------------------------------------------------ */

function calculateCounts() {
  var counts = {};
  var groupKeys = Object.keys(allOptions);

  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var options = allOptions[groupKey];
    counts[groupKey] = {};

    var otherFilters = {};
    var filterKeys = Object.keys(activeFilters);
    for (var f = 0; f < filterKeys.length; f++) {
      if (filterKeys[f] !== groupKey) {
        otherFilters[filterKeys[f]] = activeFilters[filterKeys[f]];
      }
    }

    var baseMatches = getMatchingProducts(otherFilters);

    for (var o = 0; o < options.length; o++) {
      var optionValue = options[o].value;
      var testFilters = {};
      Object.keys(otherFilters).forEach(function (k) {
        testFilters[k] = otherFilters[k];
      });
      testFilters[groupKey] = [optionValue];

      var count = 0;
      for (var m = 0; m < baseMatches.length; m++) {
        if (productMatchesAllFilters(baseMatches[m], testFilters)) {
          count++;
        }
      }
      counts[groupKey][optionValue] = count;
    }
  }
  return counts;
}

/* ------------------------------------------------------------------ */
/* Option extraction from product index                                */
/* ------------------------------------------------------------------ */

function extractUniqueValues(arr, accessor) {
  var seen = {};
  var result = [];
  for (var i = 0; i < arr.length; i++) {
    var vals = accessor(arr[i]);
    if (!vals) continue;
    if (!Array.isArray(vals)) vals = [vals];
    for (var j = 0; j < vals.length; j++) {
      var v = vals[j];
      if (v !== null && v !== undefined && v !== '' && !seen[v]) {
        seen[v] = true;
        result.push(v);
      }
    }
  }
  return result.sort();
}

function buildAllOptions() {
  allOptions = {};

  // Colour: extracted from product index colours array (names)
  allOptions.colour = extractUniqueValues(products, function (p) {
    return (p.colours || []).map(function (c) {
      return typeof c === 'string' ? c : c.name;
    });
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.mountType = extractUniqueValues(products, function (p) {
    return p.mountTypes;
  }).map(function (v) { return { value: v, label: v }; });

  var shapesAndStyles = [];
  var ssSeen = {};
  for (var i = 0; i < products.length; i++) {
    var shapes = products[i].shapes || [];
    var styles = products[i].styles || [];
    var combined = shapes.concat(styles);
    for (var j = 0; j < combined.length; j++) {
      if (!ssSeen[combined[j]]) {
        ssSeen[combined[j]] = true;
        shapesAndStyles.push(combined[j]);
      }
    }
  }
  shapesAndStyles.sort();
  allOptions.shapeStyle = shapesAndStyles.map(function (v) {
    return { value: v, label: v };
  });

  allOptions.widthBand = WIDTH_BANDS.map(function (b) {
    return { value: b.key, label: b.label };
  });

  allOptions.depthBand = DEPTH_BANDS.map(function (b) {
    return { value: b.key, label: b.label };
  });

  allOptions.priceBand = PRICE_BANDS.map(function (b) {
    return { value: b.key, label: b.label };
  });

  allOptions.overflow = extractUniqueValues(products, function (p) {
    return p.overflow;
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.tapHole = extractUniqueValues(products, function (p) {
    return p.tapHole;
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.tray = extractUniqueValues(products, function (p) {
    return p.tray;
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.finish = extractUniqueValues(products, function (p) {
    /* F1 (staff answers 2026-09-02): every product's finish feeds the facet — including
       single-finish products (fixed-property model; Conscious Organic appears once the
       custom.finish backfill lands). The old ">1 distinct" rule predated real finish data. */
    var f = (p.finish || []).map(normaliseFinish);
    return f.filter(function (v, i) { return f.indexOf(v) === i; });
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.roomType = extractUniqueValues(products, function (p) {
    return p.roomTypes;
  }).map(function (v) { return { value: v, label: v }; });
}

/* ------------------------------------------------------------------ */
/* DOM rendering                                                       */
/* ------------------------------------------------------------------ */

const GROUP_LABELS = {
  colour: 'Colour',
  mountType: 'Mount Type',
  shapeStyle: 'Shape & Style',
  widthBand: 'Width',
  depthBand: 'Depth',
  priceBand: 'Price',
  overflow: 'Overflow',
  tapHole: 'Tap Hole',
  tray: 'Tray',
  finish: (function () {
    if (typeof document === 'undefined') return 'Finish';
    var el = document.querySelector('[data-nco-filter-group="finish"] [data-nco-group-label]');
    return el ? el.textContent.trim() : 'Finish';
  })(),
  roomType: 'Room',
};

function renderOptionHTML(groupKey, option) {
  var isCheckboxGroup = groupKey === 'shapeStyle' || groupKey === 'roomType';

  if (isCheckboxGroup) {
    var cbId = 'nco-filter-' + groupKey + '-' + escapeAttr(option.value).replace(/\s+/g, '-').toLowerCase();
    return '<label class="nco-filters__option nco-filters__option--checkbox" data-nco-filter-value="' + escapeAttr(option.value) + '">'
      + '<input type="checkbox" class="nco-filters__checkbox-input" id="' + cbId + '" aria-label="' + escapeAttr(option.label) + '">'
      + '<span class="nco-filters__checkbox-mark" aria-hidden="true"></span>'
      + '<span class="nco-filters__option-label">' + escapeHTML(option.label) + '</span>'
      + '<span class="nco-filters__count"></span>'
      + '</label>';
  }

  return '<button type="button" class="nco-filters__option nco-filters__option--button"'
    + ' data-nco-filter-value="' + escapeAttr(option.value) + '"'
    + ' aria-pressed="false">'
    + '<span class="nco-filters__option-label">' + escapeHTML(option.label) + '</span>'
    + '<span class="nco-filters__count"></span>'
    + '</button>';
}

function renderGroupOptions(groupKey) {
  // Colour options are rendered in Liquid (with swatch dots), not here
  if (groupKey === 'colour') return;

  var container = document.querySelector('[data-nco-filter-options="' + groupKey + '"]');
  if (!container) return;

  var options = allOptions[groupKey];
  if (!options || options.length === 0) {
    var group = container.closest(SELECTORS.group);
    if (group) group.hidden = true;
    return;
  }

  var html = '';
  for (var i = 0; i < options.length; i++) {
    html += renderOptionHTML(groupKey, options[i]);
  }
  container.innerHTML = html;
}

function renderAllOptions() {
  var groups = Object.keys(allOptions);
  for (var i = 0; i < groups.length; i++) {
    renderGroupOptions(groups[i]);
  }
}

function updateCountDisplay(counts) {
  var groupKeys = Object.keys(counts);
  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var groupCounts = counts[groupKey];
    var optionValues = Object.keys(groupCounts);
    var groupContainer = document.querySelector('[data-nco-filter-options="' + groupKey + '"]');
    if (!groupContainer) continue;

    for (var o = 0; o < optionValues.length; o++) {
      var value = optionValues[o];
      var count = groupCounts[value];

      var optionEl = groupContainer.querySelector('[data-nco-filter-value="' + CSS.escape(value) + '"]');
      if (!optionEl) continue;

      var countEl = optionEl.querySelector('.nco-filters__count');
      if (countEl) {
        countEl.textContent = '(' + count + ')';
      }

      var isActive = activeFilters[groupKey] && activeFilters[groupKey].indexOf(value) !== -1;
      if (count === 0 && !isActive) {
        optionEl.classList.add(STATE.disabled);
        optionEl.setAttribute('aria-disabled', 'true');
        if (optionEl.tagName === 'BUTTON') {
          optionEl.disabled = true;
        }
        var cbInput = optionEl.querySelector('.nco-filters__checkbox-input');
        if (cbInput) cbInput.disabled = true;
      } else {
        optionEl.classList.remove(STATE.disabled);
        optionEl.removeAttribute('aria-disabled');
        if (optionEl.tagName === 'BUTTON') {
          optionEl.disabled = false;
        }
        var cbInput2 = optionEl.querySelector('.nco-filters__checkbox-input');
        if (cbInput2) cbInput2.disabled = false;
      }
    }
  }
}

/* ------------------------------------------------------------------ */
/* Chip management                                                     */
/* ------------------------------------------------------------------ */

function renderChips() {
  var chipsContainer = document.querySelector(SELECTORS.chips);
  if (!chipsContainer) return;

  var clearBtn = chipsContainer.querySelector(SELECTORS.clearAll);
  var existingChips = chipsContainer.querySelectorAll(SELECTORS.chip);
  for (var i = existingChips.length - 1; i >= 0; i--) {
    existingChips[i].remove();
  }

  var hasActive = false;
  var groupKeys = Object.keys(activeFilters);

  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var values = activeFilters[groupKey];
    if (!values || values.length === 0) continue;

    hasActive = true;
    for (var v = 0; v < values.length; v++) {
      var label = (GROUP_LABELS[groupKey] || groupKey) + ': ' + values[v];
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'nco-filters__chip';
      chip.setAttribute('data-nco-chip', '');
      chip.setAttribute('data-nco-chip-group', groupKey);
      chip.setAttribute('data-nco-chip-value', values[v]);
      chip.setAttribute('aria-label', 'Remove filter: ' + label);
      chip.innerHTML = '<span class="nco-filters__chip-label">' + escapeHTML(label) + '</span>'
        + '<span class="nco-filters__chip-remove" data-nco-chip-remove aria-hidden="true">'
        + '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">'
        + '<line x1="2" y1="2" x2="8" y2="8" stroke="currentColor" stroke-width="1.5"/>'
        + '<line x1="8" y1="2" x2="2" y2="8" stroke="currentColor" stroke-width="1.5"/>'
        + '</svg></span>';

      if (clearBtn) {
        chipsContainer.insertBefore(chip, clearBtn);
      } else {
        chipsContainer.appendChild(chip);
      }
    }
  }

  if (clearBtn) {
    clearBtn.hidden = !hasActive;
  }
}

/* ------------------------------------------------------------------ */
/* URL state                                                           */
/* ------------------------------------------------------------------ */

function syncFiltersToURL() {
  var params = new URLSearchParams();
  for (var i = 0; i < URL_PARAM_ORDER.length; i++) {
    var urlKey = URL_PARAM_ORDER[i];
    var filterKey = URL_KEY_TO_FILTER[urlKey];
    if (!filterKey) continue;
    var values = activeFilters[filterKey];
    if (values && values.length > 0) {
      params.set(urlKey, values.join(','));
    }
  }
  var qs = params.toString();
  var url = window.location.pathname + (qs ? '?' + qs : '');
  history.replaceState(null, '', url);
}

function readFiltersFromURL() {
  var params = new URLSearchParams(window.location.search);
  var restored = {};
  params.forEach(function (value, key) {
    var filterKey = URL_KEY_TO_FILTER[key];
    if (filterKey) {
      restored[filterKey] = value.split(',').filter(Boolean);
      if (filterKey === 'finish') restored[filterKey] = restored[filterKey].map(normaliseFinish);
    }
  });
  return restored;
}

/* Validate + collapse URL-restored filters before they bypass toggleFilter():
   drop values that aren't real options, and force single-select groups (colour,
   tray, overflow, tapHole) to one value. Guards against shared/legacy
   ?colour=Sage,Clay links and hand-edited URLs that would otherwise revert every
   card to its hero image. Returns the cleaned filters and whether anything
   changed (so init can rewrite a stale URL). */
function normaliseRestoredFilters(restored) {
  var changed = false;
  var normalised = {};

  Object.keys(restored).forEach(function (groupKey) {
    if (!activeFilters.hasOwnProperty(groupKey)) return;

    var allowed = (allOptions[groupKey] || []).map(function (o) { return o.value; });
    var values = (restored[groupKey] || []).filter(function (value) {
      return allowed.indexOf(value) !== -1;
    });

    if (values.length !== (restored[groupKey] || []).length) changed = true;

    if (SINGLE_SELECT_GROUPS[groupKey] && values.length > 1) {
      values = [values[0]];
      changed = true;
    }

    normalised[groupKey] = values;
  });

  return { filters: normalised, changed: changed };
}

/* ------------------------------------------------------------------ */
/* Filter state management                                             */
/* ------------------------------------------------------------------ */

function toggleFilter(groupKey, value) {
  if (!activeFilters[groupKey]) {
    activeFilters[groupKey] = [];
  }

  var idx = activeFilters[groupKey].indexOf(value);
  var protectedSelection = null;
  if (idx === -1) {
    if (SINGLE_SELECT_GROUPS[groupKey]) {
      activeFilters[groupKey] = [value];
    } else {
      activeFilters[groupKey].push(value);
    }
    protectedSelection = { groupKey: groupKey, value: value };
  } else {
    activeFilters[groupKey].splice(idx, 1);
  }

  pruneConflictingSelections(protectedSelection);
  applyFilters();
}

function cloneFilters(filters) {
  var copy = {};
  var keys = Object.keys(filters);
  for (var i = 0; i < keys.length; i++) {
    copy[keys[i]] = (filters[keys[i]] || []).slice();
  }
  return copy;
}

function canValueCoexistWithProtected(groupKey, value, protectedSelection) {
  if (!protectedSelection) return true;
  if (groupKey === protectedSelection.groupKey && value === protectedSelection.value) return true;

  var test = cloneFilters(activeFilters);
  test[protectedSelection.groupKey] = [protectedSelection.value];
  test[groupKey] = [value];

  return getMatchingProducts(test).length > 0;
}

function pruneConflictingSelections(protectedSelection) {
  if (protectedSelection) {
    var firstPassGroups = Object.keys(activeFilters);
    for (var pg = 0; pg < firstPassGroups.length; pg++) {
      var pgKey = firstPassGroups[pg];
      var pgValues = activeFilters[pgKey];
      if (!pgValues || pgValues.length === 0) continue;

      for (var pv = pgValues.length - 1; pv >= 0; pv--) {
        var pvValue = pgValues[pv];
        if (pgKey === protectedSelection.groupKey && pvValue === protectedSelection.value) continue;
        if (!canValueCoexistWithProtected(pgKey, pvValue, protectedSelection)) {
          pgValues.splice(pv, 1);
        }
      }
    }
  }

  var changed = true;
  var maxPasses = 5;
  while (changed && maxPasses-- > 0) {
    changed = false;
    var groupKeys = Object.keys(activeFilters);
    for (var g = 0; g < groupKeys.length; g++) {
      var groupKey = groupKeys[g];
      var values = activeFilters[groupKey];
      if (!values || values.length === 0) continue;

      var otherFilters = {};
      for (var f = 0; f < groupKeys.length; f++) {
        if (groupKeys[f] !== groupKey) {
          otherFilters[groupKeys[f]] = activeFilters[groupKeys[f]];
        }
      }

      for (var v = values.length - 1; v >= 0; v--) {
        if (
          protectedSelection &&
          groupKey === protectedSelection.groupKey &&
          values[v] === protectedSelection.value
        ) {
          continue;
        }
        var testFilter = {};
        testFilter[groupKey] = [values[v]];
        var combined = Object.assign({}, otherFilters, testFilter);
        var matchCount = getMatchingProducts(combined).length;

        if (matchCount === 0) {
          values.splice(v, 1);
          changed = true;
        }
      }
    }
  }
}

function removeFilter(groupKey, value) {
  if (!activeFilters[groupKey]) return;
  var idx = activeFilters[groupKey].indexOf(value);
  if (idx !== -1) {
    activeFilters[groupKey].splice(idx, 1);
  }
  updateOptionState(groupKey, value, false);
  applyFilters();
}

function clearAllFilters() {
  var groupKeys = Object.keys(activeFilters);
  for (var g = 0; g < groupKeys.length; g++) {
    var values = activeFilters[groupKeys[g]];
    for (var v = 0; v < values.length; v++) {
      updateOptionState(groupKeys[g], values[v], false);
    }
    activeFilters[groupKeys[g]] = [];
  }
  applyFilters();
}

function updateOptionState(groupKey, value, isActive) {
  var optionEl = document.querySelector(
    '[data-nco-filter-options="' + groupKey + '"] [data-nco-filter-value="' + CSS.escape(value) + '"]'
  );
  if (!optionEl) return;

  optionEl.classList.toggle(STATE.active, isActive);
  if (optionEl.tagName === 'BUTTON') {
    optionEl.setAttribute('aria-pressed', String(isActive));
  }
  var cbInput = optionEl.querySelector('.nco-filters__checkbox-input');
  if (cbInput) {
    cbInput.checked = isActive;
  }
}

function applyFilters() {
  var visibleProducts = getMatchingProducts(activeFilters);
  var visibleIds = visibleProducts.map(function (p) { return p.id; });
  var counts = calculateCounts();

  updateCountDisplay(counts);
  renderChips();
  updateAllActiveStates();
  syncFiltersToURL();

  // Build resolved variant data for each visible product
  var resolved = {};
  for (var i = 0; i < visibleProducts.length; i++) {
    var p = visibleProducts[i];
    resolved[p.id] = resolveFilteredVariant(p, activeFilters);
  }

  document.dispatchEvent(new CustomEvent('nco:filter:change', {
    detail: {
      visibleFamilyIds: visibleIds,
      activeFilters: activeFilters,
      totalProducts: products.length,
      visibleCount: visibleIds.length,
      resolved: resolved,
      products: products,
    },
  }));
}

function updateAllActiveStates() {
  var groupKeys = Object.keys(activeFilters);
  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var groupOptions = allOptions[groupKey] || [];
    var activeValues = activeFilters[groupKey] || [];

    for (var o = 0; o < groupOptions.length; o++) {
      var value = groupOptions[o].value;
      var isActive = activeValues.indexOf(value) !== -1;
      updateOptionState(groupKey, value, isActive);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Accordion toggle                                                    */
/* ------------------------------------------------------------------ */

function toggleAccordion(header) {
  var group = header.closest(SELECTORS.group);
  if (!group) return;

  var panel = group.querySelector('.nco-filters__group-panel');
  if (!panel) return;

  var expanded = header.getAttribute('aria-expanded') === 'true';
  header.setAttribute('aria-expanded', String(!expanded));
  group.classList.toggle(STATE.collapsed, expanded);
  panel.hidden = expanded;
}

/* ------------------------------------------------------------------ */
/* Event delegation                                                    */
/* ------------------------------------------------------------------ */

function handleContainerClick(e) {
  var header = e.target.closest(SELECTORS.groupHeader);
  if (header) {
    e.preventDefault();
    toggleAccordion(header);
    return;
  }

  var clearAll = e.target.closest(SELECTORS.clearAll);
  if (clearAll) {
    e.preventDefault();
    clearAllFilters();
    return;
  }

  var chipEl = e.target.closest(SELECTORS.chip);
  if (chipEl) {
    e.preventDefault();
    var chipGroup = chipEl.getAttribute('data-nco-chip-group');
    var chipValue = chipEl.getAttribute('data-nco-chip-value');
    if (chipGroup && chipValue) {
      removeFilter(chipGroup, chipValue);
    }
    return;
  }

  var optionEl = e.target.closest(SELECTORS.option);
  if (optionEl) {
    if (optionEl.classList.contains(STATE.disabled)) {
      e.preventDefault();
      return;
    }

    if (optionEl.tagName === 'LABEL') return;

    var groupEl = optionEl.closest(SELECTORS.group);
    if (!groupEl) return;

    var groupKey = groupEl.getAttribute('data-nco-filter-group');
    var value = optionEl.getAttribute('data-nco-filter-value');
    if (!groupKey || !value) return;

    e.preventDefault();
    toggleFilter(groupKey, value);
    return;
  }
}

function handleCheckboxChange(e) {
  var cbInput = e.target;
  if (!cbInput.classList.contains('nco-filters__checkbox-input')) return;

  var optionEl = cbInput.closest(SELECTORS.option);
  if (!optionEl) return;

  var groupEl = optionEl.closest(SELECTORS.group);
  if (!groupEl) return;

  var groupKey = groupEl.getAttribute('data-nco-filter-group');
  var value = optionEl.getAttribute('data-nco-filter-value');
  if (!groupKey || !value) return;

  toggleFilter(groupKey, value);
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

function init() {
  if (typeof window !== 'undefined' && window[NCO_FILTER_INIT_KEY]) return;
  var indexEl = document.querySelector(SELECTORS.index);
  if (!indexEl) return;

  try {
    products = JSON.parse(indexEl.textContent);
  } catch (err) {
    return;
  }

  if (!Array.isArray(products) || products.length === 0) return;

  var container = document.querySelector(SELECTORS.container);
  if (!container) return;

  buildAllOptions();

  var groupKeys = Object.keys(allOptions);
  for (var i = 0; i < groupKeys.length; i++) {
    activeFilters[groupKeys[i]] = [];
  }

  renderAllOptions();

  // Restore filters from URL params (validated + single-select-collapsed)
  var urlFilters = normaliseRestoredFilters(readFiltersFromURL());
  var urlKeys = Object.keys(urlFilters.filters);
  for (var u = 0; u < urlKeys.length; u++) {
    var fk = urlKeys[u];
    if (activeFilters.hasOwnProperty(fk)) {
      activeFilters[fk] = urlFilters.filters[fk];
    }
  }

  // If the URL carried invalid or multi-value single-select params, rewrite it
  // so the address bar reflects the actual (clean) filter state.
  if (urlFilters.changed) {
    syncFiltersToURL();
  }

  var counts = calculateCounts();
  updateCountDisplay(counts);
  updateAllActiveStates();
  renderChips();

  container.addEventListener('click', handleContainerClick);
  container.addEventListener('change', handleCheckboxChange);
  if (typeof window !== 'undefined') window[NCO_FILTER_INIT_KEY] = true;

  // Initial filter event.
  // Deferred on purpose: this engine script is rendered earlier in the DOM than
  // the grid controller (assets/nco-collection.js, loaded at the end of the
  // section), so dispatching synchronously here fires before the controller has
  // attached its nco:filter:change listener — which silently dropped the initial
  // URL-restored filter state (e.g. /collections/all?colour=Copan+Blue). Emit
  // after the current execution so every module script has registered first.
  var buildInitialDetail = function () {
    var initVisible = getMatchingProducts(activeFilters);
    var initResolved = {};
    for (var r = 0; r < initVisible.length; r++) {
      initResolved[initVisible[r].id] = resolveFilteredVariant(initVisible[r], activeFilters);
    }
    return {
      visibleFamilyIds: initVisible.map(function (p) { return p.id; }),
      activeFilters: activeFilters,
      totalProducts: products.length,
      visibleCount: initVisible.length,
      resolved: initResolved,
      products: products,
    };
  };
  var initialDetail = buildInitialDetail();
  if (typeof window !== 'undefined') window.ncoFilterInitialState = initialDetail;
  var emitInitial = function () {
    document.dispatchEvent(new CustomEvent('nco:filter:change', {
      detail: initialDetail,
    }));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', emitInitial, { once: true });
  } else {
    setTimeout(emitInitial, 0);
  }
}

/* Share the authoritative resolver with the collection controller without
   requiring a second module instance (or a stale relative import). */
if (typeof window !== 'undefined') window.ncoResolveFilteredVariant = resolveFilteredVariant;

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}

export {
  ncoComboSignature,
  ncoVariantIdFor,
  resolveFilteredVariant,
};
